import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ClipboardList, Plus, RefreshCw, FileText, CheckCircle, Clock, AlertCircle, DollarSign } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface ProcedureType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  hasCost: boolean;
  product: { name: string; price: string; priceWithTax?: string } | null;
}

interface Procedure {
  id: number;
  status: string;
  notes: string | null;
  requestedAt: string;
  completedAt: string | null;
  procedureType: { id: number; code: string; name: string };
  invoice: { id: number; invoiceNumber: string; total: string; status: string } | null;
  hasCost: boolean;
  productPrice: string | null;
  productPriceWithTax: string | null;
  invoiceTotal: string | null;
}

const statusLabels: Record<string, { label: string; color: string; icon: any }> = {
  'solicitado': { label: 'Solicitado', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'pendiente_pago': { label: 'Pendiente de Pago', color: 'bg-red-100 text-red-800', icon: DollarSign },
  'pagado_en_proceso': { label: 'Pagado - En Proceso', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  'en_proceso': { label: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: Clock },
  'completado': { label: 'Completado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'cancelado': { label: 'Cancelado', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
};

export default function StudentProcedures() {
  const { toast } = useToast();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [formData, setFormData] = useState({ procedureTypeId: '', notes: '' });
  const [selectedType, setSelectedType] = useState<ProcedureType | null>(null);

  const fetchMyProcedures = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/procedures/my-procedures', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProcedures(data);
      }
    } catch (error) {
      console.error('Error fetching procedures:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcedureTypes = async () => {
    try {
      const response = await fetch('/api/procedures/types/active?audience=alumno', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProcedureTypes(data);
      }
    } catch (error) {
      console.error('Error fetching procedure types:', error);
    }
  };

  useEffect(() => {
    fetchMyProcedures();
    fetchProcedureTypes();
  }, []);

  const handleTypeChange = (typeId: string) => {
    setFormData({ ...formData, procedureTypeId: typeId });
    const type = procedureTypes.find(t => t.id.toString() === typeId);
    setSelectedType(type || null);
  };

  const handleRequest = async () => {
    if (!formData.procedureTypeId) {
      toast({ title: 'Error', description: 'Seleccione un tipo de trámite', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch('/api/procedures/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          procedureTypeId: parseInt(formData.procedureTypeId),
          notes: formData.notes || undefined,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Trámite solicitado',
          description: result.invoiceCreated
            ? 'Se ha generado un cargo. Revisa tus pagos pendientes.'
            : 'Tu solicitud ha sido registrada.',
        });
        setShowRequestDialog(false);
        setFormData({ procedureTypeId: '', notes: '' });
        setSelectedType(null);
        fetchMyProcedures();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Error al solicitar trámite', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parseFloat(amount));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Mis Trámites</h1>
            <p className="text-muted-foreground">Solicita y da seguimiento a tus trámites académicos</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchMyProcedures} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => setShowRequestDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Solicitar Trámite
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Cargando...</div>
      ) : procedures.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tienes trámites registrados</p>
              <p className="text-sm mt-2">Haz clic en "Solicitar Trámite" para comenzar</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {procedures.map((procedure) => {
            const statusInfo = statusLabels[procedure.status] || statusLabels['solicitado'];
            const StatusIcon = statusInfo.icon;

            return (
              <Card key={procedure.id}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-full ${statusInfo.color}`}>
                        <StatusIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{procedure.procedureType.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          Solicitado el {formatDate(procedure.requestedAt)}
                        </p>
                        {procedure.notes && (
                          <p className="text-sm mt-1">{procedure.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                      {procedure.hasCost && (procedure.invoiceTotal ?? procedure.productPriceWithTax ?? procedure.productPrice) && (
                        <p className="text-sm mt-2">
                          Costo: <span className="font-medium">{formatCurrency(procedure.invoiceTotal ?? procedure.productPriceWithTax ?? procedure.productPrice!)}</span>
                        </p>
                      )}
                      {procedure.invoice && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Factura: {procedure.invoice.invoiceNumber}
                          {procedure.invoice.status === 'PAID' && ' (Pagada)'}
                        </p>
                      )}
                      {procedure.completedAt && (
                        <p className="text-xs text-green-600 mt-1">
                          Completado el {formatDate(procedure.completedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Solicitar Trámite</DialogTitle>
            <DialogDescription>Selecciona el tipo de trámite que necesitas</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Trámite *</Label>
              <Select value={formData.procedureTypeId} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar trámite" />
                </SelectTrigger>
                <SelectContent>
                  {procedureTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedType && (
              <div className="p-3 bg-muted rounded-lg space-y-2">
                {selectedType.description && (
                  <p className="text-sm">{selectedType.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {selectedType.hasCost && selectedType.product
                      ? `Costo: ${formatCurrency(selectedType.product.priceWithTax ?? selectedType.product.price)}`
                      : 'Sin costo'}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notas adicionales (opcional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Información adicional para tu solicitud..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancelar</Button>
            <Button onClick={handleRequest}>Solicitar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
